import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, Database, Pencil, Trash2 } from 'lucide-react';
import { schemasApi, appsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export function SchemasPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch schemas from API
  const {
    data: schemasData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['schemas'],
    queryFn: () => schemasApi.list(),
  });

  // Fetch apps for the mapping
  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Delete schema mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => schemasApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
      setDeleteId(null);
    },
  });

  const handleEdit = (schemaId: string) => {
    navigate(`/data/${schemaId}`);
  };

  const schemas = schemasData?.items || [];
  const apps = appsData?.items || [];
  const appMap = new Map(apps.map((app) => [app.id, app.name]));

  const getFieldCount = (fields: string) => {
    try {
      const parsed = JSON.parse(fields);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Modèles de Données</h2>
          <p className="text-sm text-secondary-500">Définissez vos structures de données</p>
        </div>
        <Button onClick={() => navigate('/data/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau Modèle
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-secondary-500">Chargement des modèles...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && schemas.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300 py-12 dark:border-secondary-700">
          <Database className="h-12 w-12 text-secondary-400" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900 dark:text-white">
            Aucun modèle de données
          </h3>
          <p className="mt-2 text-sm text-secondary-500">
            Créez votre premier modèle pour définir vos structures de données.
          </p>
          <Button className="mt-4" onClick={() => navigate('/data/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Créer un modèle
          </Button>
        </div>
      )}

      {/* Schemas grid */}
      {!isLoading && !error && schemas.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {schemas.map((schema) => (
            <div
              key={schema.id}
              className="rounded-xl border border-secondary-200 bg-white p-5 dark:border-secondary-800 dark:bg-secondary-900"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Database className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900 dark:text-white">{schema.name}</h3>
                    <p className="text-sm text-secondary-500">
                      {appMap.get(schema.app_id) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              {schema.description && (
                <p className="mt-3 text-sm text-secondary-500 line-clamp-2">{schema.description}</p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-secondary-400">
                  {getFieldCount(schema.fields)} champs
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(schema.id)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Dialog open={deleteId === schema.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(schema.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Supprimer le modèle</DialogTitle>
                        <DialogDescription>
                          Êtes-vous sûr de vouloir supprimer "{schema.name}" ? Cette action est irréversible.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>
                          Annuler
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => deleteMutation.mutate(schema.id)}
                          disabled={deleteMutation.isPending}
                        >
                          {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Supprimer
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
